import { closestCorners, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { motion } from "motion/react";
import { useTodoStore } from "../../store/todoStore";
import { KANBAN_COLUMNS, type KanbanColumnId } from "../../types/kanban";
import { isTodoCompleted } from "../../utils/todoRights";
import KanbanColumn from "./KanbanColumn";

type Props = {
  disabled?: boolean;
  onOpenTodo?: (id: string) => void;
};

export function KanbanBoard({ disabled = false, onOpenTodo }: Props) {
  const { todos, moveTodoToColumn, isLoading } = useTodoStore();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (disabled) return;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;
    const activeTodo = todos.find((todo) => todo.id === activeId);
    if (!activeTodo || activeTodo.relationship === "viewer" || isTodoCompleted(activeTodo)) return;

    // 如果拖动到同一位置，不做处理
    if (activeId === overId) return;

    // 判断是否是列
    const isColumn = KANBAN_COLUMNS.some((col) => col.id === overId);

    if (isColumn) {
      if (overId === "todo") return;
      // 拖动到列上，移动任务到该列
      try {
        await moveTodoToColumn(activeId, overId as KanbanColumnId);
      } catch (_error) {
        // 错误已在 API 层处理
      }
    } else {
      // 拖动到另一个任务上，找到该任务所在的列
      for (const column of KANBAN_COLUMNS) {
        const columnTodos = column.getTaskIds(todos);
        if (columnTodos.includes(overId)) {
          if (column.id === "todo") return;
          try {
            await moveTodoToColumn(activeId, column.id as KanbanColumnId);
          } catch (_error) {
            // 错误已在 API 层处理
          }
          break;
        }
      }
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full">
      {isLoading && todos.length === 0 ? (
        <output className="grid gap-6 md:grid-cols-3" aria-label="看板加载中">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-64 animate-pulse rounded-xl bg-muted" />
          ))}
        </output>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
          <div className="flex gap-6 overflow-x-auto pb-4">
            {KANBAN_COLUMNS.map((column) => {
              const columnTodos = todos.filter((todo) => column.getTaskIds(todos).includes(todo.id));

              return (
                <KanbanColumn
                  key={column.id}
                  column={column}
                  todos={columnTodos}
                  disabled={disabled}
                  onOpenTodo={onOpenTodo}
                />
              );
            })}
          </div>
        </DndContext>
      )}
    </motion.div>
  );
}
